import {
  CopyAllOutlined,
  KeyboardArrowDown,
  TextSnippetOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tab,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import CaseIcon from "../../../assets/icons/case.svg";
import Button from "../../../components/buttons/Button";
import ProgressBar from "../../../components/graphs/ProgressBar";
import Dialog from "../../../components/misc/Dialog";
import InfoIcon from "../../../components/misc/InfoIcon";
import OnboardingModal from "../../../components/misc/OnboardingModal";
import { StyledTabs } from "../../../components/misc/StyledTabs";
import { useAppContext } from "../../../contexts/AppContext";
import { useCase } from "../../../contexts/CaseContext";
import { useError } from "../../../contexts/ErrorContext";
import {
  default as Translation,
  default as Translations,
} from "../../../lang/en/pages.json";
import Main from "../../../layouts/Main";
import { CaseMockResponse } from "../../../onboarding/mock";
import { copyToClipboard, scrollToBottom } from "../../../utils";
import { RelatedCaseRow, TableFilters } from "../types";
import Overview from "./Overview";
import RelatedCasesTable from "./RelatedCasesTable";
import Settlement from "./Settlement";
import Trial from "./Trial";
import FullScreenLoader from "../../../components/misc/FullscreenLoader";

const SimilarCaseScoreInfoDialog: React.FC<{
  open: boolean;
  data: { title: string; score: number; content: string } | null;
  onClose: () => void;
}> = ({ open, data, onClose }) => {
  const { title, score, content } = data || {
    title: "",
    score: 0,
    content: "",
  };

  return (
    <Dialog
      closeAlignment="right"
      open={open}
      onClose={onClose}
      maxWidth={600}
      sx={{ minWidth: 600 }}
    >
      <Stack spacing={2}>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography
            textTransform={"capitalize"}
            variant="h6"
            fontWeight={"bold"}
            color={"#404943"}
          >
            {title}
          </Typography>
          <Stack direction={"row"} spacing={0.5}>
            <Typography variant="h6" fontWeight={"light"} color={"#404943"}>
              {Translation.pages.dashboard_page.dialogs.scores_info.score}
              {": "}
            </Typography>
            <Typography variant="h6" fontWeight={"bold"} color={"#404943"}>
              {score}%
            </Typography>
          </Stack>
        </Stack>
        <Divider orientation="horizontal" />
        <Typography fontSize={14} color={"#404943"}>
          {content}
        </Typography>
      </Stack>
    </Dialog>
  );
};

const SimilarCaseSummaryDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  caseName: string;
  summary: string;
  caseId: string;
}> = ({ open, onClose, summary, caseId, caseName }) => {
  const { showSuccess, showError } = useError();

  const handleCopy = (id: string) => {
    try {
      copyToClipboard(id);
      showSuccess(
        Translations.pages.dashboard_page.tabs.overview.messages.case_id_copied
      );
    } catch (e) {
      console.error(e);
      showError(
        Translations.pages.dashboard_page.tabs.overview.errors.case_id_copy
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={600}>
      <Stack alignItems={"center"} justifyContent={"center"} spacing={1}>
        <TextSnippetOutlined />
        <Typography fontWeight={"medium"}>{caseName}</Typography>
        <Stack spacing={1} p={2} borderRadius={4}>
          <Stack
            alignItems={"center"}
            sx={{ backgroundColor: "#00000012" }}
            direction={"row"}
            spacing={1}
            borderRadius={2}
            px={1}
          >
            <Typography fontWeight={"bold"} fontSize={14} color={"#404943"}>
              {Translation.pages.dashboard_page.dialogs.summary.case_id}
            </Typography>
            <Typography fontSize={14} color={"#404943"}>
              {caseId}
            </Typography>
            {caseId !== "N/A" && caseId && (
              <IconButton onClick={() => handleCopy(caseId)}>
                <CopyAllOutlined sx={{ fontSize: "1rem" }} />
              </IconButton>
            )}
          </Stack>
          <Typography fontSize={14} color={"#404943"}>
            {summary}
          </Typography>
        </Stack>
      </Stack>
    </Dialog>
  );
};

const SimilarCaseScoreDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSimilarCaseScoreInfoClick: (index: number) => void;
  data: { name: string; score: number }[];
}> = ({ open, onClose, data, onSimilarCaseScoreInfoClick }) => {
  return (
    <Dialog
      sx={{ width: "40%", maxWidth: 600, mx: "auto" }}
      open={open}
      onClose={onClose}
      maxWidth={500}
    >
      <Typography mb={2} fontWeight={"bold"} variant={"h6"} color={"#404943"}>
        {Translation.pages.dashboard_page.dialogs.scores.title}
      </Typography>
      <Stack spacing={3}>
        <Typography variant="body2" color={"#404943"}>
          {Translation.pages.dashboard_page.dialogs.scores.description}
        </Typography>

        <Stack spacing={1}>
          {data.map((d, index) => (
            <Stack
              key={index}
              justifyContent={"space-between"}
              alignItems={"center"}
              width={"100%"}
            >
              <Typography fontSize={14} color={"#404943"}>
                {d.name}
              </Typography>
              <Stack
                alignItems={"center"}
                width={"80%"}
                direction={"row"}
                spacing={1}
              >
                <ProgressBar
                  withShadow={true}
                  fontColor="#00796B"
                  emptyColor="white"
                  filledColor="#D0E8D7"
                  progressPlacement="center"
                  progress={typeof d.score === "number" ? d.score : null}
                  height={16}
                />
                <IconButton onClick={() => {}}>
                  <InfoIcon
                    fontSize={18}
                    backgroundColor="#00796B"
                    textColor="white"
                    onClick={() => onSimilarCaseScoreInfoClick(index)}
                  />
                </IconButton>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Dialog>
  );
};

const Dashboard: React.FC = () => {
  const { setBreadcrumbs, setSelectedPage } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { caseMeta, currentOutcome, revisionMeta, isCaseLoading } =
    useCase() || {};

  const [similarCasesExpanded, setSimilarCasesExpanded] = useState(false);
  const [similarCaseScoreDialogOpen, setSimilarCaseScoreDialogOpen] =
    useState(false);
  const [similarCaseScoreInfoDialogOpen, setSimilarCaseScoreInfoDialogOpen] =
    useState(false);
  const [similarCaseScoreData, setSimilarCaseScoreData] = useState<
    { name: string; explanation: string; score: number }[]
  >([]);
  const [similarCaseScoreInfoData, setSimilarCaseScoreInfoData] = useState<{
    title: string;
    score: number;
    content: string;
  } | null>(null);
  const [similarCaseId, setSimilarCaseId] = useState<string>("");
  const [similarCaseSummaryData, setSimilarCaseSummaryData] =
    useState<string>("");
  const [similarCaseName, setSimilarCaseName] = useState<string>("");
  const [similarCaseSummaryDialogOpen, setSimilarCaseSummaryDialogOpen] =
    useState(false);

  const { id: caseId, revisionId } = useParams();

  useEffect(() => {
    if (!caseMeta || !revisionMeta) {
      return;
    }

    if (!revisionId && !revisionMeta?.status) {
      navigate(`/cases/${caseId}/files`);
    }

    if (caseMeta.status === "error" || revisionMeta.status === "error") {
      navigate(`/cases/${caseId}/files`);
    }
  }, [revisionMeta, revisionId, caseMeta]);

  const innerPage = location.pathname.split("/").pop();
  const tableFilters: TableFilters = {
    resolutionType: null,
    caseResult: null,
  };
  if (innerPage === "settlement") {
    tableFilters.resolutionType = "settlement";
  }
  if (innerPage === "trial") {
    tableFilters.resolutionType = "trial";
  }

  useEffect(() => {
    if (caseMeta) {
      setBreadcrumbs([
        {
          label: Translation.common.header.breadcrumb.items[0].label,
          href: "/cases",
          icon: <img src={CaseIcon} />,
        },
        {
          label: caseMeta?.name ?? "",
        },
      ]);

      if (caseMeta.status === "processing") {
        navigate(`/cases/${caseId}/status`);
        return;
      }
      if (revisionMeta && !revisionMeta.status) {
        navigate(`/cases/${caseId}/files`);
        return;
      }

      setSelectedPage("Dashboard");
    }
  }, [caseMeta]);

  const openSimilarCaseScoreDialog = (similarCase: RelatedCaseRow) => {
    setSimilarCaseScoreData(
      Object.keys(similarCase.relevance).map((key) => ({
        name: key,
        score: similarCase?.relevance[key]?.value ?? 0,
        explanation: similarCase?.relevance[key]?.explanation ?? "",
      }))
    );
    setSimilarCaseScoreDialogOpen(true);
  };

  const openSimilarCaseSummaryDialog = (similarCase: RelatedCaseRow) => {
    setSimilarCaseSummaryDialogOpen(true);
    setSimilarCaseSummaryData(similarCase.summary);
    setSimilarCaseId(similarCase?.external_id ?? "N/A");
    setSimilarCaseName(similarCase.name);
  };

  const getInitialTab = () => {
    const path = location.pathname;
    if (path.includes("/dashboard/settlement")) return 1;
    if (path.includes("/dashboard/trial")) return 2;
    return 0;
  };
  const [selectedTab, setSelectedTab] = useState(getInitialTab());

  useEffect(() => {
    setSelectedTab(getInitialTab());
  }, [location.pathname]);
const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
  const newTab = ["overview", "settlement", "trial"][newValue];
  setSelectedTab(newValue);

  // Keep revisionId in URL if it exists
  if (revisionId) {
    navigate(`/cases/${caseId}/revisions/${revisionId}/dashboard/${newTab}`);
  } else {
    navigate(`/cases/${caseId}/dashboard/${newTab}`);
  }
};

  const handleExpandSimilarCases = () => {
    setSimilarCasesExpanded(!similarCasesExpanded);
    if (!similarCasesExpanded) {
      setTimeout(() => scrollToBottom(), 200);
    }
  };

  if (isCaseLoading || !caseMeta || revisionMeta?.status !== "ready") {
    return <FullScreenLoader />;
  }

  return (
    <Main id={"dashboard"}>
      <React.Fragment>
        <Box
          id="nav"
          mb={4}
          display={"flex"}
          justifyContent={"center"}
          width={"100%"}
        >
          <StyledTabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
          >
            <Tab
              label={
                <Typography variant="body2">
                  {
                    Translation.pages.dashboard_page.navigation.items.overview
                      .label
                  }
                </Typography>
              }
            />
            <Tab
              label={
                <Typography variant="body2">
                  {
                    Translation.pages.dashboard_page.navigation.items.settlement
                      .label
                  }
                </Typography>
              }
            />
            <Tab
              label={
                <Typography variant="body2">
                  {
                    Translation.pages.dashboard_page.navigation.items.trial
                      .label
                  }
                </Typography>
              }
            />
          </StyledTabs>
        </Box>
        <Stack spacing={4} width={"100%"}>
          <Routes>
            <Route path="/" element={<Overview />} />
            <Route path="overview" element={<Overview />} />
            <Route path="settlement" element={<Settlement />} />
            <Route path="trial" element={<Trial />} />
          </Routes>
          {innerPage === "overview" && (
            <Button onClick={handleExpandSimilarCases} variant="topnav">
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <KeyboardArrowDown
                  style={{
                    transform:
                      similarCasesExpanded || caseId === CaseMockResponse.id
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
                <Typography fontSize={14}>
                  {similarCasesExpanded || caseId === CaseMockResponse.id
                    ? Translation.pages.dashboard_page.tabs.overview.buttons
                        .similar_cases.toggle_hide
                    : Translation.pages.dashboard_page.tabs.overview.buttons
                        .similar_cases.toggle_show}
                </Typography>
              </Stack>
            </Button>
          )}
          <Accordion
            expanded={
              ["trial", "settlement"].includes(innerPage ?? "") ||
              similarCasesExpanded ||
              caseId === CaseMockResponse.id
            }
            sx={{
              borderRadius: 4,
            }}
          >
            <AccordionSummary sx={{ display: "none" }}></AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <RelatedCasesTable
                id="relevant_cases"
                filters={tableFilters}
                data={currentOutcome?.similar_cases ?? []}
                onSimilarityScoreClick={(similarCase: any) =>
                  openSimilarCaseScoreDialog(similarCase)
                }
                onSimilaritySummaryClick={(similarCase: any) =>
                  openSimilarCaseSummaryDialog(similarCase)
                }
              />
            </AccordionDetails>
          </Accordion>
          <SimilarCaseScoreDialog
            open={similarCaseScoreDialogOpen}
            onClose={() => setSimilarCaseScoreDialogOpen(false)}
            onSimilarCaseScoreInfoClick={(index) => {
              setSimilarCaseScoreInfoData({
                title: similarCaseScoreData[index].name,
                score: similarCaseScoreData[index].score,
                content: similarCaseScoreData[index].explanation,
              });
              setSimilarCaseScoreInfoDialogOpen(true);
            }}
            data={similarCaseScoreData}
          />
          <SimilarCaseSummaryDialog
            caseName={similarCaseName}
            open={similarCaseSummaryDialogOpen}
            onClose={() => setSimilarCaseSummaryDialogOpen(false)}
            summary={similarCaseSummaryData}
            caseId={similarCaseId}
          />
          <SimilarCaseScoreInfoDialog
            open={similarCaseScoreInfoDialogOpen}
            onClose={() => setSimilarCaseScoreInfoDialogOpen(false)}
            data={similarCaseScoreInfoData}
          />
        </Stack>
      </React.Fragment>
    </Main>
  );
};

export default Dashboard;
